import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { RythemIcon } from "../../icons/icons";
import graphsFa from "./HealthMonitoringGraphs-fa.json";
import graphsEn from "./HealthMonitoringGraphs-en.json";
import graphsAr from "./HealthMonitoringGraphs-ar.json";
import graphsTr from "./HealthMonitoringGraphs-tr.json";
const HeartRateGraph: React.FC = () => {
    const { t, i18n } = useTranslation("healthMonitoring");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [isExpanded, setIsExpanded] = useState(false);
    const [pulseAnimation, setPulseAnimation] = useState(true);

    const heartRateData = [68, 71, 69, 72, 110, 115, 90, 75, 72, 70, 73, 72];
    const timeLabels = [
        t("graphs.heartRate.times.0"),
        t("graphs.heartRate.times.1"),
        t("graphs.heartRate.times.2"),
        t("graphs.heartRate.times.3"),
        t("graphs.heartRate.times.4"),
        t("graphs.heartRate.times.5"),
        t("graphs.heartRate.times.6"),
        t("graphs.heartRate.times.7"),
        t("graphs.heartRate.times.8"),
    ];

    return (
        <div className="mt-4 relative" dir={isRTL ? "rtl" : "ltr"}>
            {/* Interactive Heart Rate Display */}
            <motion.div className="relative flex items-center justify-center mb-6 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                {/* Pulse Rings */}
                {pulseAnimation && (
                    <>
                        <motion.div
                            className="absolute rounded-full bg-[#0072CE]/5"
                            animate={{ scale: [1, 1.5, 1.8], opacity: [0.7, 0.3, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                            style={{ width: 120, height: 120 }}
                        />
                        <motion.div
                            className="absolute rounded-full bg-[#0072CE]/10"
                            animate={{ scale: [1, 1.3, 1.6], opacity: [0.7, 0.4, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.3, ease: "easeOut" }}
                            style={{ width: 120, height: 120 }}
                        />
                    </>
                )}

                {/* Central Display */}
                <motion.div
                    className="relative z-10 bg-gradient-to-br from-[#0072CE]/20 to-[#0072CE]/5 rounded-full w-[120px] h-[120px] flex flex-col items-center justify-center shadow-lg border border-[#0072CE]/20"
                    animate={{
                        boxShadow: pulseAnimation ? ["0 0 0 rgba(0,114,206,0.2)", "0 0 20px rgba(0,114,206,0.4)", "0 0 0 rgba(0,114,206,0.2)"] : "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                    transition={{ duration: 1, repeat: Infinity }}
                >
                    <motion.div
                        className="text-4xl font-bold bg-gradient-to-r from-[#0072CE] to-[#0072CE]/80 bg-clip-text text-transparent"
                        animate={{ scale: pulseAnimation ? [1, 1.03, 1] : 1 }}
                        transition={{ duration: 0.6, repeat: Infinity }}
                    >
                        {t("graphs.heartRate.currentBPM")}
                    </motion.div>
                    <div className="text-sm text-[#0072CE]/80 font-medium">BPM</div>
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-white/90 text-[10px] text-[#0072CE] px-2 py-0.5 rounded-full shadow-sm border border-[#0072CE]/10">
                        {t("graphs.heartRate.status.resting")}
                    </div>
                </motion.div>

                {/* Animation Toggle Button */}
                <motion.button
                    className={`absolute -top-2 ${isRTL ? "-left-2" : "-right-2"} bg-white w-6 h-6 rounded-full flex items-center justify-center shadow-md border border-gray-100 text-[#9EA3A9] z-20`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setPulseAnimation(!pulseAnimation);
                    }}
                    whileHover={{ scale: 1.1, backgroundColor: "#f8f8f8" }}
                    whileTap={{ scale: 0.95 }}
                >
                    {pulseAnimation ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                    )}
                </motion.button>
            </motion.div>

            {/* Heart Rate Indicators */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                    <div className="text-xs text-[#9EA3A9]">{t("graphs.heartRate.min")}</div>
                    <div className="text-sm font-medium text-[#0072CE]">{t("graphs.heartRate.minValue")}</div>
                </div>
                <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                    <div className="text-xs text-[#9EA3A9] pt-1 mt-1">{t("graphs.heartRate.average")}</div>
                    <div className="text-sm font-medium text-[#0072CE]">{t("graphs.heartRate.averageValue")}</div>
                </div>
                <div className="bg-gradient-to-br from-[#ECEFF1]/80 to-[#ECEFF1]/30 p-2 rounded-xl text-center">
                    <div className="text-xs text-[#9EA3A9] pt-1 mt-1">{t("graphs.heartRate.max")}</div>
                    <div className="text-sm font-medium text-[#0072CE]">{t("graphs.heartRate.maxValue")}</div>
                </div>
            </div>

            {/* ECG Rhythm Display */}
            <div className="relative">
                <div className={`text-xs text-[#9EA3A9] mb-3 flex flex-row justify-between items-center`}>
                    <span>{t("graphs.heartRate.ecgRhythm")}</span>
                    <span className={`text-[#32D74B] flex items-center flex-row`}>
                        <svg className={`w-3 h-3 ${isRTL ? "mr-1" : "ml-1"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        {t("graphs.heartRate.status.normal")}
                    </span>
                </div>

                <div className="h-12 w-full bg-[#ECEFF1]/50 rounded-xl overflow-hidden relative">
                    <RythemIcon />
                </div>
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="mt-4">
                        <div className={`text-xs text-[#9EA3A9] mb-1 text-start`}>{t("graphs.heartRate.todayChart")}</div>
                        <div className="h-32 bg-[#ECEFF1]/50 rounded-xl p-3 relative">
                            {/* Grid Lines */}
                            <div className="absolute inset-0 flex flex-col justify-between p-3 pointer-events-none">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="w-full h-px bg-[#9EA3A9]/10"></div>
                                ))}
                            </div>

                            {/* Heart Rate Chart */}
                            <div className="relative h-full w-full flex items-end">
                                <svg viewBox="0 0 100 100" className="w-full h-full absolute inset-0">
                                    <defs>
                                        <linearGradient id="heartRateGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                            <stop offset="0%" stopColor="#0072CE" stopOpacity="0.3" />
                                            <stop offset="100%" stopColor="#0072CE" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <motion.path
                                        d={`M0,${100 - (heartRateData[0] - 60) * 1.5} ${heartRateData.map((hr, i) => `L${(i + 1) * 8.3},${100 - (hr - 60) * 1.5}`).join(" ")}`}
                                        fill="none"
                                        stroke="#0072CE"
                                        strokeWidth="2"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 1.5, ease: "easeInOut" }}
                                    />
                                    <motion.path
                                        d={`M0,${100 - (heartRateData[0] - 60) * 1.5} ${heartRateData.map((hr, i) => `L${(i + 1) * 8.3},${100 - (hr - 60) * 1.5}`).join(" ")} L100,100 L0,100 Z`}
                                        fill="url(#heartRateGradient)"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 1, delay: 0.5 }}
                                    />
                                </svg>

                                {/* Time Labels */}
                                <div
                                    className={`absolute bottom-0 ${isRTL ? "right-0 left-0" : "left-0 right-0"} flex ${
                                        isRTL ? "flex-row-reverse" : "flex-row"
                                    } justify-between text-[10px] text-[#9EA3A9]`}
                                >
                                    {timeLabels.map((time, i) => (
                                        <span key={i} className={i % 2 === 0 ? "" : "invisible md:visible"}>
                                            {time}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Health Insights */}
                        <div className="mt-3 p-3 bg-gradient-to-r from-[#0072CE]/10 to-[#0072CE]/5 rounded-xl">
                            <div className={`text-xs font-medium text-[#0072CE] mb-1 text-start`}>{t("graphs.heartRate.insights.title")}</div>
                            <div className={`text-xs text-[#002B5C] text-start`}>{t("graphs.heartRate.insights.text")}</div>
                        </div>

                        {/* Heart Rate Zones */}
                        <div className="mt-3 p-3 bg-[#F5F7FA] rounded-xl">
                            <h4 className={`text-sm font-medium text-[#0072CE] mb-2 text-start`}>{t("graphs.heartRate.zones.title")}</h4>
                            <div className={`text-xs text-[#4A5568] mb-3 text-start`}>{t("graphs.heartRate.zones.description")}</div>

                            <div className="relative h-10 bg-gradient-to-r from-[#32D74B] via-[#FFD60A] to-[#FF3B30] rounded-lg mb-2 overflow-hidden">
                                {[0, 20, 40, 60, 80].map((pos, i) => (
                                    <div key={i} className="absolute top-0 bottom-0 w-px bg-white/50" style={{ left: `${pos}%` }} />
                                ))}

                                <div
                                    className="absolute top-0 bottom-0 w-1 bg-white shadow-md z-10 flex items-center justify-center"
                                    style={{ left: `${Math.min(((72 - 60) / (220 - 60)) * 100, 100)}%` }}
                                >
                                    <div className="absolute -top-6 -translate-x-1/2 bg-white text-[10px] font-medium text-[#0072CE] px-1.5 py-0.5 rounded shadow-sm">
                                        {t("graphs.heartRate.currentBPM")}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-5 gap-1 text-center text-[9px]">
                                <div className="text-[#32D74B]">
                                    {t("graphs.heartRate.zones.resting")}
                                    <br />
                                    {t("graphs.heartRate.zones.restingRange")}
                                </div>
                                <div className="text-[#66D4CF]">
                                    {t("graphs.heartRate.zones.fatBurn")}
                                    <br />
                                    {t("graphs.heartRate.zones.fatBurnRange")}
                                </div>
                                <div className="text-[#0072CE]">
                                    {t("graphs.heartRate.zones.cardio")}
                                    <br />
                                    {t("graphs.heartRate.zones.cardioRange")}
                                </div>
                                <div className="text-[#FFD60A]">
                                    {t("graphs.heartRate.zones.aerobic")}
                                    <br />
                                    {t("graphs.heartRate.zones.aerobicRange")}
                                </div>
                                <div className="text-[#FF3B30]">
                                    {t("graphs.heartRate.zones.peak")}
                                    <br />
                                    {t("graphs.heartRate.zones.peakRange")}
                                </div>
                            </div>

                            <div className={`mt-3 flex items-center justify-between flex-row`}>
                                <div className="text-xs text-[#4A5568]">{t("graphs.heartRate.zones.currentZone")}</div>
                                <div className="text-xs font-medium text-[#32D74B]">{t("graphs.heartRate.zones.resting")}</div>
                            </div>
                            <div className={`text-xs text-[#4A5568] mt-1 text-start`}>{t("graphs.heartRate.zones.currentZoneDescription")}</div>
                        </div>

                        {/* HRV */}
                        <div className="mt-3 p-3 bg-[#F5F7FA] rounded-xl">
                            <h4 className={`text-sm font-medium text-[#0072CE] mb-2 text-start`}>{t("graphs.heartRate.hrv.title")}</h4>

                            <div className={`flex items-center justify-between mb-3 flex-row`}>
                                <div className="text-xs text-[#4A5568]">{t("graphs.heartRate.hrv.today")}</div>
                                <div className="text-sm font-medium text-[#32D74B]">{t("graphs.heartRate.hrv.todayValue")}</div>
                            </div>

                            <div className="h-24 bg-[#ECEFF1]/50 rounded-xl p-3 relative mb-2">
                                <div className="flex items-end justify-between h-full">
                                    {[58, 62, 59, 65, 63, 60, 65].map((value, index) => (
                                        <div key={index} className="flex flex-col items-center">
                                            <div className="w-4 bg-gradient-to-t from-[#0072CE] to-[#00E5FF] rounded-t-sm" style={{ height: `${(value / 80) * 100}%` }}></div>
                                            <div className="text-[9px] text-[#9EA3A9] mt-1">{t(`graphs.heartRate.hrv.days.${index}`)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`text-xs text-[#4A5568] text-start`}>{t("graphs.heartRate.hrv.description")}</div>

                            <div className={`mt-2 flex items-center justify-between flex-row`}>
                                <div className="text-xs text-[#4A5568]">{t("graphs.heartRate.hrv.recoveryStatus")}</div>
                                <div className="text-xs font-medium text-[#32D74B]">{t("graphs.heartRate.hrv.recoveryStatusValue")}</div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default HeartRateGraph;
